import React, { useCallback, useMemo } from 'react'
import { Box } from '@mui/material'
import { createColumnHelper } from '@tanstack/react-table'
import { generatePath } from 'react-router-dom'

import { useGetAcqPayments } from '@/services'
import { FetchErrorMessage } from '@/components'
import { TableComponent } from '@/components/table'
import { AcqPayment } from '@/types.ts'
import { DefaultFiltersState, TableSort } from '@/stores'
import { router } from '@/router.tsx'
import { APP_ROUTES } from '@/constants.ts'
import { useIsMobile } from '@/hooks'
import { useAppDispatch, useAppSelector } from '@/stores/hooks.ts'
import { acquiringPaymentsActions, acquiringPaymentSelectors } from '@/stores/slices/acquiring-payments-slice.ts'
import { userSelectors } from '@/stores/slices/user-slice.ts'

import {
  defaultSettings,
  getColumnsSettings,
  getStaticColumns,
  KEY_PAGE,
  PartialAcqPayment,
} from './default-settings.tsx'

const columnHelper = createColumnHelper<AcqPayment>()
const defaultColumns = getColumnsSettings(columnHelper)
const staticColumns = getStaticColumns(columnHelper)

export const AcquiringPaymentsTable = (): React.ReactElement => {
  const { data, isLoading, isFetching, isError, refetch } = useGetAcqPayments()
  const { page, showCount, sort } = useAppSelector(acquiringPaymentSelectors.filters)
  const settings = useAppSelector((state) => userSelectors.personalSettings(state, KEY_PAGE))
  const isMobile = useIsMobile()
  const dispatch = useAppDispatch()

  const handlePaginationParam = (param: keyof DefaultFiltersState) => (value: number) =>
    dispatch(
      acquiringPaymentsActions.setFilter({
        filterKey: param,
        filterValue: value,
      })
    )
  const handleSort = (nextSort: TableSort) =>
    dispatch(
      acquiringPaymentsActions.setFilter({
        filterKey: 'sort',
        filterValue: nextSort,
      })
    )

  const handleViewTransaction = useCallback(
    (row: AcqPayment) => () => {
      router.navigate(generatePath(APP_ROUTES.AcqPaymentsView, { id: row.id.toString() }))
    },
    []
  )

  const acqPaymentCols = useMemo(() => {
    const format = isMobile ? 'mobile' : 'desktop'

    const desktopSettings = (settings?.[format] ?? Object.keys(defaultSettings)) as Array<keyof PartialAcqPayment>

    const dynamicSettings = desktopSettings.map((e) => defaultColumns[e])
    return [...dynamicSettings, ...Object.values(staticColumns)]
  }, [settings, isMobile])

  return (
    <FetchErrorMessage isFetching={isFetching} isError={isError} refetch={refetch}>
      <Box mt={3}>
        <TableComponent<AcqPayment>
          rowKey="id"
          columns={acqPaymentCols}
          isLoading={isLoading || isFetching}
          page={page}
          sort={sort}
          size={showCount}
          data={data?.data?.content ?? []}
          totalPages={data?.data.totalPages}
          onPageChange={handlePaginationParam('page')}
          onSizeChange={handlePaginationParam('showCount')}
          onSort={handleSort}
          columnPinning={{ right: ['refund-action'] }}
          onRowClick={handleViewTransaction}
        />
      </Box>
    </FetchErrorMessage>
  )
}
